'use client';

import { useEffect } from 'react';
import { SectionHeader } from '../components/SectionHeader';
import { AdminDataTable } from '../components/AdminDataTable';
import type { AdminServiceRow } from '../context/AdminAllDataContext';
import { useAdminAllData } from '../context/AdminAllDataContext';
import { Button } from '@/core/presentation/components/ui/Button';
import { Plus } from 'lucide-react';

function formatDate(isoDate: string) {
  return new Date(isoDate + 'T12:00:00').toLocaleDateString('pt-BR');
}

const columns = [
  { key: 'id', header: 'ID' },
  { key: 'ownerDisplayName', header: 'Usuário', render: (r: AdminServiceRow) => r.ownerDisplayName },
  {
    key: 'clientDisplayLabel',
    header: 'Cliente',
    render: (r: AdminServiceRow) => r.clientDisplayLabel,
  },
  {
    key: 'serviceTypeLabel',
    header: 'Tipo',
    render: (r: AdminServiceRow) => r.serviceTypeLabel,
  },
  {
    key: 'service_date',
    header: 'Data do serviço',
    render: (r: AdminServiceRow) => formatDate(r.service_date),
  },
  {
    key: 'next_maintenance_date',
    header: 'Próx. manutenção',
    render: (r: AdminServiceRow) => formatDate(r.next_maintenance_date),
  },
  {
    key: 'value',
    header: 'Valor',
    render: (r: AdminServiceRow) =>
      r.value === null
        ? '—'
        : r.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
  },
];

export default function ServicesPage() {
  const {
    state: { enrichedServices, usersLoading, servicesLoading, usersError, servicesError },
    events: { refreshUsers, refreshServices },
  } = useAdminAllData();

  useEffect(() => {
    void Promise.all([refreshUsers(), refreshServices()]);
  }, [refreshUsers, refreshServices]);

  const loadingInitial =
    (usersLoading || servicesLoading) && enrichedServices.length === 0;
  const errorMessage = usersError ?? servicesError;

  return (
    <div>
      <SectionHeader
        title="Serviços"
        subtitle="Controle de serviços realizados e agendados"
        actions={
          <Button className="bg-admin-navy hover:bg-admin-navy-mid text-white">
            <Plus className="h-4 w-4 mr-1" />
            Novo serviço
          </Button>
        }
      />
      {errorMessage ? (
        <p className="text-sm text-destructive mb-4" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {loadingInitial ? (
        <p className="text-sm text-admin-text-secondary py-8">Carregando serviços…</p>
      ) : (
        <AdminDataTable
          data={enrichedServices}
          columns={columns}
          searchKeys={['id', 'serviceTypeLabel', 'equipment_reference', 'ownerDisplayName', 'clientDisplayLabel']}
        />
      )}
    </div>
  );
}
